import { Component, computed, inject, viewChild } from '@angular/core';
import { combineLatest, concatMap, map, of, Subscription, switchMap, tap } from 'rxjs';
import { AddressFormComponent } from '../address-form/address-form.component';
import { RoundIconComponent } from '../round-icon/round-icon.component';
import { AuthService } from '../../core/services/auth.service';
import { AddressService } from '../../core/services/address.service';
import { AccountAddressesService } from '../../core/services/account/account-addresses.service';
import { AddressBookService } from '../../core/services/address-book.service';
import { ErrorsService } from '../../core/services/errors.service';
import { IconNameType } from '../icons/icon-name-type.enum';
import { AddressValidationResult } from '../../core/models/core/address-validation-result.model';
import { GenericObject } from '../../core/models/core/generic-object';
import { Address } from '../../core/models/core/address.model';
import { AsyncButtonDirective } from '../directives/async-button.directive';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { AuditService } from '../../core/services/audit.service';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'cwo-edit-address-modal',
  standalone: true,
  imports: [AddressFormComponent, RoundIconComponent, AsyncButtonDirective, TranslocoDirective],
  templateUrl: './edit-address-modal.component.html',
  styleUrl: './edit-address-modal.component.scss',
})
export class EditAddressModalComponent {
  modalService = inject(ModalService);
  authService = inject(AuthService);
  addressService = inject(AddressService);
  accountAddressesService = inject(AccountAddressesService);
  addressBookService = inject(AddressBookService);
  private auditService = inject(AuditService);
  toastService = inject(ToastService);
  translocoService = inject(TranslocoService);

  savedAddress = computed(() => this.addressBookService.state());
  editTitle = computed(() => {
    return `"${this.savedAddress()?.name}"`;
  });
  errorsService = inject(ErrorsService);

  iconNameType = IconNameType;
  isAddressFormValid: boolean = false;
  validationResponse?: AddressValidationResult;
  serverErrors: GenericObject<string[]> | undefined;
  loadingSubscription: Subscription | null = null;

  readonly addressFormComponent = viewChild.required<AddressFormComponent>('addressFormComponent');

  closeModal() {
    this.modalService.closeModal(ModalName.EditAddress);
  }

  handleAddressFormValidation(isFormValid: boolean) {
    this.isAddressFormValid = isFormValid;
  }

  handleFormChanging() {
    this.validationResponse = undefined;
    this.serverErrors = undefined;
  }

  handleOverrideAddress() {
    this.onSubmit(true);
  }

  handleNoThankyou() {
    this.auditService.createAudit(() => "Cancel button clicked on Change Store window.");
    this.validationResponse = undefined;
  }

  validateExistingAddressAndProceed = (address: Address) => {
    return this.addressService.validateExistingAddress(address).pipe(
      switchMap((validationResponse) => {
        // Check if validationResponse contains errors
        if (validationResponse?.error) {
          return of(validationResponse); // Return validationResponse to handle it later
        }

        return this.accountAddressesService.updateAddress(address).pipe(
          map(() => validationResponse) // Continue passing validation response
        );
      }),
      switchMap((validationResponse) => {
        if (!validationResponse?.error) {
          return this.accountAddressesService
            .fetchAddresses()
            .pipe(map(() => validationResponse));
        }
        // Return observable that completes without further processing
        return of(validationResponse);
      })
    );
  };

  onSubmit(overrideAddress?: boolean) {
    this.auditService.createAudit(() => "Done button clicked on Edit Address modal.");
    this.serverErrors = undefined;

    if (this.validationResponse && !overrideAddress) {
      this.addressFormComponent().toggleHighlightOverrideMessage();
      return;
    }
    this.validationResponse = undefined;

    setTimeout(() => {
      if (!this.isAddressFormValid) {
        if (!this.isAddressFormValid) {
          this.addressFormComponent().enableErrors();
        }
        return;
      }

      const address = new Address().deserialize(
        this.addressFormComponent().addressForm.value
      );

      //trimming address name since server allows multiple values with the same name to be added when we have a space in the end
      address.name = address.name?.trim();

      if (overrideAddress) {
        address.force = true;
        
        this.loadingSubscription = this.accountAddressesService
          .updateAddress(address)
          .pipe(
            tap(() => {
                  this.toastService.showSuccess(
                    this.translocoService.translate('route.account.address_book.address_updated_successfully')
                  );
                  }),
            concatMap(() =>
              combineLatest([
                this.accountAddressesService.fetchAddresses(),
                this.authService.fetchAccount(true),
              ])
            )
          )
          .subscribe({
            next: () => {
              this.closeModal();
              this.loadingSubscription = null;
            },
            error: (error: any) => {
              const parsedError = this.errorsService.parseErrorResponse(error);
              this.serverErrors = parsedError.errors;
              this.loadingSubscription = null;
            },
          });
      } else {
        this.loadingSubscription = this.validateExistingAddressAndProceed(address).subscribe({
          next: (validationResponse) => {
            if (validationResponse?.error) {
              this.addressFormComponent().enableErrors();
              this.validationResponse = validationResponse;
            } else {
               this.toastService.showSuccess(
                    this.translocoService.translate('route.account.address_book.address_updated_successfully')
                  );
              this.closeModal();
            }
            this.loadingSubscription = null;
          },
          error: (error) => {
            const parsedError = this.errorsService.parseErrorResponse(error);
            this.serverErrors = parsedError.errors;
            this.loadingSubscription = null;
          },
        });
      }
    }, 0);
  }
}
